import React, { useState } from 'react';
import { ChevronDown, ChevronRight, RefreshCw } from 'lucide-react';
import FeriasStatusBadge from './FeriasStatusBadge';

const STATUS_FILTROS = [
  { value: 'todos',    label: 'Todos' },
  { value: 'vencida',  label: 'Vencidas' },
  { value: 'vencendo', label: 'Vencendo' },
  { value: 'pendente', label: 'Pendente' },
];

function formatarData(iso) {
  if (!iso) return '—';
  const [ano, mes, dia] = iso.split('-');
  return `${dia}/${mes}/${ano}`;
}

function DiasRestantes({ dias }) {
  if (dias < 0) {
    return <span className="text-red-600 font-medium">{Math.abs(dias)} dias vencida</span>;
  }
  if (dias <= 30) {
    return <span className="text-yellow-600 font-medium">Vence em {dias} dia{dias !== 1 ? 's' : ''}</span>;
  }
  return <span className="text-gray-500">{dias} dias restantes</span>;
}

function PeriodoLinha({ periodo }) {
  const rowBg = {
    vencida:  'bg-red-50',
    vencendo: 'bg-yellow-50',
    pendente: 'bg-blue-50',
    em_dia:   '',
    concluida:'',
  }[periodo.status] ?? '';

  return (
    <tr className={`text-sm ${rowBg}`}>
      <td className="px-4 py-2 text-gray-500">Período {periodo.periodo_numero}</td>
      <td className="px-4 py-2 whitespace-nowrap">
        {formatarData(periodo.periodo_aquisitivo_inicio)} – {formatarData(periodo.periodo_aquisitivo_fim)}
      </td>
      <td className="px-4 py-2 whitespace-nowrap text-gray-600">
        até {formatarData(periodo.periodo_concessivo_fim)}
      </td>
      <td className="px-4 py-2">
        <DiasRestantes dias={periodo.dias_restantes_concessivo} />
      </td>
      <td className="px-4 py-2">
        <FeriasStatusBadge status={periodo.status} />
      </td>
    </tr>
  );
}

function LinhaFuncionario({ item }) {
  const [expandido, setExpandido] = useState(false);

  const rowBorder = {
    vencida:  'border-l-4 border-l-red-500',
    vencendo: 'border-l-4 border-l-yellow-500',
    pendente: 'border-l-4 border-l-blue-400',
  }[item.pior_status] ?? '';

  return (
    <>
      <tr
        className={`cursor-pointer hover:bg-gray-50 transition-colors ${rowBorder}`}
        onClick={() => setExpandido(v => !v)}
      >
        <td className="px-4 py-3">
          {expandido
            ? <ChevronDown className="h-4 w-4 text-gray-400" />
            : <ChevronRight className="h-4 w-4 text-gray-400" />
          }
        </td>
        <td className="px-4 py-3 font-medium text-gray-900">{item.funcionario.nome}</td>
        <td className="px-4 py-3 text-gray-500 text-sm">{item.funcionario.matricula}</td>
        <td className="px-4 py-3 text-gray-500 text-sm">{item.funcionario.cargo}</td>
        <td className="px-4 py-3 text-gray-500 text-sm whitespace-nowrap">
          {formatarData(item.funcionario.dt_admissao)}
        </td>
        <td className="px-4 py-3">
          <FeriasStatusBadge status={item.pior_status} />
        </td>
        <td className="px-4 py-3 text-sm text-gray-500">
          {item.periodos.length} período{item.periodos.length !== 1 ? 's' : ''} em aberto
        </td>
      </tr>

      {expandido && (
        <tr>
          <td colSpan={7} className="px-0 py-0 bg-gray-50 border-b border-gray-200">
            <div className="px-8 py-3">
              <p className="text-xs text-gray-500 font-medium mb-2 uppercase tracking-wide">
                Períodos aquisitivos
              </p>
              <table className="w-full">
                <thead>
                  <tr className="text-xs text-gray-400 uppercase">
                    <th className="px-4 pb-1 text-left font-medium">Ciclo</th>
                    <th className="px-4 pb-1 text-left font-medium">Período Aquisitivo</th>
                    <th className="px-4 pb-1 text-left font-medium">Prazo Concessivo</th>
                    <th className="px-4 pb-1 text-left font-medium">Situação</th>
                    <th className="px-4 pb-1 text-left font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {item.todos_periodos.map((p) => (
                    <PeriodoLinha key={p.periodo_numero} periodo={p} />
                  ))}
                </tbody>
              </table>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * Tabela principal de vencimento de férias com filtros por status
 * e linhas expansíveis mostrando todos os períodos do funcionário.
 */
export default function FeriasVencimentoTabela({ dados, loading, error, statusAtivo, onStatusChange, onRecarregar }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Cabeçalho com filtros */}
      <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mr-2">
          Vencimentos de Férias
        </div>

        <div className="flex gap-1 bg-gray-100 rounded-lg p-0.5">
          {STATUS_FILTROS.map(f => (
            <button
              key={f.value}
              onClick={() => onStatusChange(f.value)}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                statusAtivo === f.value
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <button
          onClick={onRecarregar}
          disabled={loading}
          className="ml-auto p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-50"
          title="Atualizar"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Estados de carregamento / erro / vazio */}
      {loading && (
        <div className="py-12 flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500" />
        </div>
      )}

      {!loading && error && (
        <div className="py-8 text-center text-sm text-red-500">
          Erro ao carregar dados de vencimento.
        </div>
      )}

      {!loading && !error && dados.length === 0 && (
        <div className="py-12 text-center text-sm text-gray-400">
          Nenhum funcionário com férias em situação crítica.
        </div>
      )}

      {/* Tabela */}
      {!loading && !error && dados.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr className="text-xs text-gray-500 uppercase tracking-wide">
                <th className="w-8 px-4 py-2" />
                <th className="px-4 py-2 text-left font-medium">Funcionário</th>
                <th className="px-4 py-2 text-left font-medium">Matrícula</th>
                <th className="px-4 py-2 text-left font-medium">Cargo</th>
                <th className="px-4 py-2 text-left font-medium">Admissão</th>
                <th className="px-4 py-2 text-left font-medium">Situação</th>
                <th className="px-4 py-2 text-left font-medium">Períodos</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {dados.map(item => (
                <LinhaFuncionario key={item.funcionario.id} item={item} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
